import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
// currency helpers
import { useCurrencyUtils } from "../../utils/currencyUtils";
import api from "../../lib/api.ts";
import Pagination from "../common/Pagination";
import { useAuth } from "../../context/AuthContext";
import SellerInvoicePaymentPopup from "../common/SellerInvoicePaymentPopup";
import { CreditCard } from "lucide-react";

const SellerInvoices = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);

  const { user } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchSellerInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          sellerId: user.seller.id,
          filters: {},
        };

        const response = await api.get(`/sellerinvoices/paginated`, { params });

        const data = response?.data?.data || response?.data || [];
        setInvoices(Array.isArray(data) ? data : []);

        // Update pagination state from API response
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.page,
            totalPages: response.data.pagination.totalPages,
            totalItems: response.data.pagination.totalItems,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }
      } catch (err) {
        setError(translate("sellerInvoices.messages.failedToLoad"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerInvoices();
  }, [currentPage, pageSize]);

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusMap = {
      Paid: translate("sellerInvoices.status.paid"),
      Pending: translate("sellerInvoices.status.pending"),
      "Partial Payment": translate("sellerInvoices.status.partialPayment"),
      Cancelled: translate("sellerInvoices.status.cancelled"),
      Refunded: translate("sellerInvoices.status.refunded"),
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );
  };

  return (
    <div
      className='bg-card rounded-lg shadow '
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >

      <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]'>
        {/* Standardized container with fixed height and Gate Pass padding */}
        <div className='flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft min-h-[120px] lg:min-h-[72px]'>

          <div className='flex flex-col justify-center'>
            <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
              {translate("sellerInvoices.heading")}
            </h2>
          </div>

          {/* Right side spacer to maintain the footprint of the search bar pages */}
          <div className='hidden lg:block'></div>

        </div>
      </div>

      {loading ? (
        <div className='p-6 text-sm text-description'>
          {translate("sellerInvoices.messages.loading")}
        </div>
      ) : error ? (
        <div className='p-6 text-sm text-red-600'>{error}</div>
      ) : invoices.length === 0 ? (
        <div className='p-6 text-sm text-center text-description'>
          {translate("sellerInvoices.messages.noInvoicesFound")}
        </div>
      ) : (
        <div className="px-3 py-6">
          <div className='overflow-x-auto rounded-lg'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className="bg-page border-b border-default py-4">
                <tr >
                  <th
                    className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                      } text-xs font-medium text-description uppercase tracking-wider`}
                  >
                    {translate("sellerInvoices.tableHeaders.vehicle")}
                  </th>

                  <th
                    className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                      } text-xs font-medium text-description uppercase tracking-wider`}
                  >
                    {translate("sellerInvoices.tableHeaders.totalAmount")}
                  </th>
                  <th
                    className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                      } text-xs font-medium text-description uppercase tracking-wider`}
                  >
                    {translate("sellerInvoices.tableHeaders.status")}
                  </th>
                  <th
                    className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                      } text-xs font-medium text-description uppercase tracking-wider`}
                  >
                    {translate("sellerInvoices.tableHeaders.date")}
                  </th>
                  <th
                    className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                      } text-xs font-medium text-description uppercase tracking-wider`}
                  >
                    {translate("sellerInvoices.tableHeaders.action")}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {invoices.map((inv) => {
                  // Get vehicle name from sellerInvoiceDetails array
                  const vehicleName = inv?.vehicle?.name ?? "-";
                  const id = inv?.id ?? "-";
                  const date = inv?.createdAt ?? "-";
                  const amount = inv?.totalAmount ?? 0;
                  const status = inv?.invoicePaymentStatus?.name ?? "-";

                  return (
                    <tr key={inv?.id} className="border-b border-default hover:bg-page/60 transition-colors relative">
                      <td className='px-4 py-5 text-sm text-white'>
                        {vehicleName}
                      </td>

                      <td className='px-4 py-5 text-sm font-medium text-description'>
                        {displayPriceWithSymbol(convertPrice(amount))}
                      </td>
                      <td className='px-4 py-5 text-xs'>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status === "Paid"
                            ? "bg-green-700 text-white"
                            : status === "Pending"
                              ? "bg-yellow-700 text-white"
                              : status === "Partial Payment"
                                ? "bg-blue-700 text-white"
                                : status === "Cancelled"
                                  ? "bg-red-700 text-white"
                                  : status === "Refunded"
                                    ? "bg-purple-700 text-white"
                                    : "bg-gray-700 text-white"
                            }`}
                        >
                          {getStatusText(status)}
                        </span>
                      </td>
                      <td className='px-4 py-5 text-sm text-description'>
                        {formatDate(date)}
                      </td>
                      <td className='px-4 py-5 text-sm relative h-10'>
                        {(status === "Pending" ||
                          status === "Partial Payment") && (
                            <button
                              onClick={() => handlePayNow(inv)}
                              className="absolute right-1/2 top-1/2 -translate-y-1/2 h-10 w-10 hover:w-40 flex items-center bg-blue-600 border border-blue-500 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                            >
                              {/* Wrapper with fixed layout */}
                              <div className="flex items-center h-full w-full">
                                {/* Icon Container: Always centered */}
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                  <CreditCard size={20} />
                                </div>

                                {/* Text: Slides out */}
                                <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium whitespace-nowrap">
                                  {translate("sellerInvoices.buttons.payNow")}
                                </span>
                              </div>
                            </button>

                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {pagination.totalPages > 1 && (
              <div className='px-6 py-4 border-t border-gray-200'>
                <Pagination
                  totalPages={pagination.totalPages}
                  currentPage={pagination.currentPage}
                  setCurrentPage={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {/* Payment Popup */}
      <SellerInvoicePaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType='invoice'
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default SellerInvoices;
